import { Link } from "react-router-dom";
import { companyInfo, serviceAreas } from "@/mocks/websiteData";

export default function Footer() {
  const quickLinks = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/products", label: "Products & Pricing" },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact" },
  ];

  const footerServices = [
    { name: "On-Grid Solar", icon: "ri-sun-line" },
    { name: "Off-Grid Solar", icon: "ri-battery-2-charge-line" },
    { name: "Commercial Solar", icon: "ri-building-4-line" },
    { name: "Drone Spray", icon: "ri-flight-takeoff-line" },
    { name: "EV Charging", icon: "ri-charging-pile-line" },
    { name: "Solar Water Pumps", icon: "ri-water-flash-line" },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-secondary-800 to-secondary-950 text-secondary-200 overflow-hidden">
      {/* Subtle decorative top glow line */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-accent-400/60 to-transparent"></div>

      {/* Ambient glow orbs */}
      <div className="absolute -top-20 left-1/4 w-64 h-64 rounded-full bg-primary-500/[0.04] blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-32 right-1/4 w-80 h-80 rounded-full bg-accent-500/[0.03] blur-3xl pointer-events-none"></div>

      {/* Subtle dot pattern overlay */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle, currentColor 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }}></div>

      <div className="relative z-10 section-padding pt-14 md:pt-20 pb-10 md:pb-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-10">
          {/* Logo & About - spans 4 cols */}
          <div className="sm:col-span-2 lg:col-span-4">
            <div className="mb-5">
              <img
                src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/127f5de18fcb6e991f81b7eaee1b9910.jpeg"
                alt={`${companyInfo.shortName} Logo`}
                className="h-12 md:h-14 w-auto object-contain brightness-110"
              />
            </div>
            <p className="text-sm text-secondary-400 leading-relaxed mb-5 max-w-md">
              {companyInfo.shortName} delivers solar energy systems, agricultural drone spraying, and EV charging infrastructure across Telangana. One partner for clean energy and smart farming.
            </p>

            {/* Three Pillar Highlights */}
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-300">
                <i className="ri-sun-line w-3 h-3 flex items-center justify-center"></i>
                Solar Energy
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 text-accent-300">
                <i className="ri-flight-takeoff-line w-3 h-3 flex items-center justify-center"></i>
                Drone Spray
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-secondary-500/30 border border-secondary-500/30 text-background-50/80">
                <i className="ri-charging-pile-line w-3 h-3 flex items-center justify-center"></i>
                EV Charging
              </span>
            </div>

            {/* Cert/Badge */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-background-50/[0.04] border border-background-50/[0.06] max-w-fit">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 shrink-0">
                <i className="ri-verified-badge-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-background-50/90">Govt. Registered</p>
                <p className="text-[11px] text-secondary-400">GST: {companyInfo.gstNo}</p>
              </div>
            </div>
          </div>

          {/* Quick Links - spans 2 cols */}
          <div className="lg:col-span-2">
            <h4 className="font-heading font-semibold text-background-50 text-sm mb-5 tracking-wide">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="group inline-flex items-center gap-2 text-sm text-secondary-400 hover:text-primary-300 transition-all"
                  >
                    <span className="w-0 group-hover:w-3 h-[1.5px] bg-primary-400/60 transition-all duration-200 shrink-0"></span>
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - spans 3 cols */}
          <div className="lg:col-span-3">
            <h4 className="font-heading font-semibold text-background-50 text-sm mb-5 tracking-wide">
              Our Services
            </h4>
            <ul className="space-y-2.5">
              {footerServices.map((s) => (
                <li key={s.name}>
                  <Link
                    to="/services"
                    className="group inline-flex items-center gap-2 text-sm text-secondary-400 hover:text-primary-300 transition-all"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-secondary-500 group-hover:text-primary-400 transition-colors shrink-0">
                      <i className={`${s.icon} w-3 h-3 flex items-center justify-center text-xs`}></i>
                    </span>
                    <span>{s.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact - spans 3 cols */}
          <div className="lg:col-span-3">
            <h4 className="font-heading font-semibold text-background-50 text-sm mb-5 tracking-wide">
              Get In Touch
            </h4>
            <div className="space-y-4">
              {/* Phone */}
              <a
                href="tel:8019051311"
                className="group flex items-center gap-3 p-3 rounded-lg bg-background-50/[0.03] border border-background-50/[0.05] hover:bg-primary-500/[0.08] hover:border-primary-500/20 transition-all"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 shrink-0 group-hover:bg-primary-500/25 transition-colors">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-xs text-secondary-400">Call Us</p>
                  <p className="text-sm font-semibold text-background-50 group-hover:text-primary-300 transition-colors">8019051311</p>
                </div>
              </a>

              {/* Email */}
              <a
                href={`mailto:${companyInfo.email}`}
                className="group flex items-center gap-3 p-3 rounded-lg bg-background-50/[0.03] border border-background-50/[0.05] hover:bg-accent-500/[0.08] hover:border-accent-500/20 transition-all"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400 shrink-0 group-hover:bg-accent-500/25 transition-colors">
                  <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-xs text-secondary-400">Email Us</p>
                  <p className="text-sm font-semibold text-background-50 group-hover:text-accent-300 transition-colors">{companyInfo.email}</p>
                </div>
              </a>

              {/* Address */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-background-50/[0.03] border border-background-50/[0.05]">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-500/20 text-secondary-400 shrink-0 mt-0.5">
                  <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-xs text-secondary-400 mb-0.5">Address</p>
                  <p className="text-sm text-secondary-300 leading-relaxed">{companyInfo.address}</p>
                </div>
              </div>
            </div>

            {/* Service Areas */}
            <h4 className="font-heading font-semibold text-background-50 text-sm mt-7 mb-3 tracking-wide">
              Service Areas
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {serviceAreas.map((area) => (
                <span
                  key={area}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-background-50/[0.04] text-secondary-400 border border-background-50/[0.06] hover:bg-primary-500/[0.08] hover:text-primary-300 hover:border-primary-500/20 transition-all cursor-default"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-background-50/[0.06]">
        <div className="relative z-10 section-padding py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-secondary-500">
            &copy; {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
          </p>

          {/* Social icons in bottom bar */}
          <div className="flex items-center gap-1">
            <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-primary-500/15 text-secondary-400 hover:text-primary-400 transition-all">
              <i className="ri-facebook-fill w-3.5 h-3.5 flex items-center justify-center"></i>
            </a>
            <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent-500/15 text-secondary-400 hover:text-accent-400 transition-all">
              <i className="ri-instagram-line w-3.5 h-3.5 flex items-center justify-center"></i>
            </a>
            <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-primary-500/15 text-secondary-400 hover:text-primary-400 transition-all">
              <i className="ri-whatsapp-line w-3.5 h-3.5 flex items-center justify-center"></i>
            </a>
            <span className="w-px h-4 mx-2 bg-background-50/[0.08]"></span>
            <p className="text-[11px] text-secondary-500">
              Reg: {companyInfo.regNo}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}